import { Component, OnInit, ViewChild, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ProductService, Product, PriceOption, WcCompareResult, WcFieldDifference } from '../../services/product.service';
import { Observable, map } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { WebsiteService } from '../../services/website.service';

type SortColumn = 'id' | 'sku' | 'name' | 'price' | 'stock' | 'status' | null;
type SortDirection = 'asc' | 'desc' | null;

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NotificationComponent,
        TruncatePipe
    ]
})
export class ProductsComponent implements OnInit {
    products$: Observable<Product[]>;
    availableDomains: string[] = [];
    editingDomains: { [productId: string]: boolean } = {};
    sortColumn: SortColumn = null;
    sortDirection: SortDirection = null;
    filterText: string = '';
    showAddProductModal: boolean = false;
    isAddingProduct: boolean = false;

    // WooCommerce sync check state
    showSyncModal: boolean = false;
    syncCheckLoading: boolean = false;
    syncToWooLoading: boolean = false;
    syncFromWooLoading: boolean = false;
    syncCompareResult: WcCompareResult | null = null;
    syncProduct: Product | null = null;

    newProduct = {
        sku: '',
        naam_nl: '',
        price: 0,
        stock_quantity: 0,
        stock_status: 'instock' as 'instock' | 'outofstock' | 'onbackorder'
    };

    constructor(
        private productService: ProductService, 
        private notificationService: NotificationService,
        private zone: NgZone,
        private router: Router,
        private websiteService: WebsiteService
    ) {
        this.products$ = this.productService.getProducts().pipe(
            map(products => {
                let filtered = this.filterProducts(products);
                return this.sortProducts(filtered);
            })
        );
    }

    async ngOnInit() {
        // Load available domains
        this.availableDomains = await this.websiteService.getWebsites();
    }

    filterProducts(products: Product[]): Product[] {
        if (!this.filterText || this.filterText.trim() === '') {
            return products;
        }

        const filter = this.filterText.toLowerCase().trim();
        return products.filter(product => {
            // Filter by ID
            const idMatch = product.id?.toLowerCase().includes(filter) || false;
            
            // Filter by name (acf.naam_nl)
            const nameMatch = product.acf?.naam_nl?.toLowerCase().includes(filter) || false;

            return idMatch || nameMatch;
        });
    }

    onFilterChange() {
        // Re-filter and re-sort products
        this.products$ = this.productService.getProducts().pipe(
            map(products => {
                let filtered = this.filterProducts(products);
                return this.sortProducts(filtered);
            })
        );
    }

    sortProducts(products: Product[]): Product[] {
        if (!this.sortColumn || !this.sortDirection) {
            return products;
        }

        return [...products].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (this.sortColumn) {
                case 'id':
                    aValue = a.id || '';
                    bValue = b.id || '';
                    break;
                case 'sku':
                    aValue = a.sku || '';
                    bValue = b.sku || '';
                    break;
                case 'name':
                    aValue = a.acf?.naam_nl || '';
                    bValue = b.acf?.naam_nl || '';
                    break;
                case 'price':
                    aValue = typeof a.price === 'string' ? parseFloat(a.price) : (a.price || 0);
                    bValue = typeof b.price === 'string' ? parseFloat(b.price) : (b.price || 0);
                    break;
                case 'stock':
                    aValue = a.stock_quantity || 0;
                    bValue = b.stock_quantity || 0;
                    break;
                case 'status':
                    aValue = a.stock_status || '';
                    bValue = b.stock_status || '';
                    break;
                default:
                    return 0;
            }

            // Handle string comparison
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.localeCompare(bValue);
                return this.sortDirection === 'asc' ? comparison : -comparison;
            }

            // Handle number comparison
            const comparison = aValue - bValue;
            return this.sortDirection === 'asc' ? comparison : -comparison;
        });
    }

    onSort(column: SortColumn) {
        if (this.sortColumn === column) {
            // Toggle direction if same column
            if (this.sortDirection === 'asc') {
                this.sortDirection = 'desc';
            } else if (this.sortDirection === 'desc') {
                this.sortColumn = null;
                this.sortDirection = null;
            } else {
                this.sortDirection = 'asc';
            }
        } else {
            // New column, start with ascending
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }

        // Re-filter and re-sort products
        this.products$ = this.productService.getProducts().pipe(
            map(products => {
                let filtered = this.filterProducts(products);
                return this.sortProducts(filtered);
            })
        );
    }

    getSortIcon(column: SortColumn): string {
        if (this.sortColumn !== column) {
            return 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4';
        }
        return this.sortDirection === 'asc' 
            ? 'M5 15l7-7 7 7' 
            : 'M19 9l-7 7-7-7';
    }

    formatPrice(price: number | string | undefined): string {
        if (!price) return '€0.00';
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return `€${numPrice.toFixed(2)}`;
    }

    getStockStatusClass(status: string): string {
        switch (status) {
            case 'instock':
                return 'text-green-400 bg-green-400/10';
            case 'outofstock':
                return 'text-red-400 bg-red-400/10';
            case 'onbackorder':
                return 'text-yellow-400 bg-yellow-400/10';
            default:
                return 'text-gray-400 bg-gray-400/10';
        }
    }

    getStockStatusLabel(status: string): string {
        switch (status) {
            case 'instock':
                return 'In Stock';
            case 'outofstock':
                return 'Out of Stock';
            case 'onbackorder':
                return 'On Backorder';
            default:
                return 'Unknown';
        }
    }

    async toggleStatus(product: Product) {
        try {
            await this.productService.toggleStockStatus(product.id!, product.stock_status);
            console.log(product.stock_status);
            const newStatus = product.stock_status === 'instock' ? 'out of stock' : 'in stock';
            this.notificationService.show({
                type: 'success',
                message: `Product ${product.acf.naam_nl} is now ${newStatus}`
            });
        } catch (error) {
            console.error('Error updating status:', error);
            this.notificationService.show({
                type: 'error',
                message: 'Failed to update product status'
            });
        }
    }

    startEditingPrice(product: Product): void {
        // Set editing flag and initialize edit value with current price
        product.isEditingPrice = true;
        product.editPrice = parseFloat(product.price?.toString() || '0');
    }

    savePrice(product: Product): void {
        // Validate and update the product price
        if (product.editPrice !== null && product.editPrice !== undefined) {
            const originalPrice = product.price;
            product.price = product.editPrice;
            
            // Call service to update the price in the backend
            if (product.id) {
                this.productService.updateProductPrice(product.id, product.price).subscribe({
                    next: () => {
                        this.notificationService.show({
                            message: 'Price updated successfully',
                            type: 'success'
                        });
                    },
                    error: (error: any) => {
                        // Revert to original price on error
                        product.price = originalPrice;
                        this.notificationService.show({
                            message: 'Failed to update price',
                            type: 'error'
                        });
                        console.error('Error updating price:', error);
                    }
                });
            } else {
                this.notificationService.show({
                    message: 'Cannot update price: Product ID is missing',
                    type: 'error'
                });
            }
        }
        
        // Exit editing mode
        product.isEditingPrice = false;
    }

    // Methods for handling price options
    
    hasPriceOptions(product: Product): boolean {
        return !!product.price_options && product.price_options.length > 0;
    }
    
    startEditingPriceOption(option: PriceOption): void {
        // First set all other options to not editing to avoid multiple edits at once
        option.isEditing = true;
        option.editPrice = parseFloat(option.price?.toString() || '0');
    }
    
    savePriceOption(product: Product, option: PriceOption): void {
        // First, explicitly close editing mode for this option
        option.isEditing = false;
        
        // Use NgZone to ensure Angular's change detection catches this state change
        this.zone.run(() => {
            // Make sure options stay visible
            product.showPriceOptions = true;
        });
        
        // Proceed with saving only if we have a valid price
        if (option.editPrice !== null && option.editPrice !== undefined) {
            const originalPrice = option.price;
            option.price = option.editPrice;
            
            if (product.id && product.price_options) {
                // Make a deep copy to avoid reference issues
                const updatedOptions = JSON.parse(JSON.stringify(product.price_options));
                
                this.productService.updateProductPriceOptions(product.id, updatedOptions).subscribe({
                    next: () => {
                        this.notificationService.show({
                            message: 'Price option updated successfully',
                            type: 'success'
                        });
                        
                        // Use NgZone to ensure Angular's change detection catches this state change
                        this.zone.run(() => {
                            product.showPriceOptions = true;
                        });
                    },
                    error: (error: any) => {
                        // Revert to original price on error
                        option.price = originalPrice;
                        this.notificationService.show({
                            message: 'Failed to update price option',
                            type: 'error'
                        });
                        console.error('Error updating price option:', error);
                        
                        // Use NgZone to ensure Angular's change detection catches this state change
                        this.zone.run(() => {
                            product.showPriceOptions = true;
                        });
                    }
                });
            } else {
                this.notificationService.show({
                    message: 'Cannot update price option: Product ID is missing',
                    type: 'error'
                });
            }
        }
    }

    togglePriceOptionsView(product: Product): void {
        this.zone.run(() => {
            product.showPriceOptions = !product.showPriceOptions;
        });
    }

    editProduct(product: Product): void {
        if (product.id) {
            this.router.navigate(['/webshop/edit', product.id]);
        } else {
            this.notificationService.show({
                type: 'error',
                message: 'Cannot edit product: Product ID is missing'
            });
        }
    }

    toggleDomainEditor(product: Product): void {
        if (!product.id) return;
        const productId = product.id;
        this.editingDomains[productId] = !this.editingDomains[productId];
    }

    isDomainSelected(product: Product, domain: string): boolean {
        const domains = (product as any)['domain'] || (product as any)['domains'] || [];
        if (Array.isArray(domains)) {
            return domains.includes(domain);
        }
        return domains === domain;
    }

    toggleDomainSelection(product: Product, domain: string): void {
        if (!product.id) return;
        
        const domains = (product as any)['domain'] || (product as any)['domains'] || [];
        let updatedDomains: string[] = [];
        
        if (Array.isArray(domains)) {
            updatedDomains = [...domains];
        } else if (domains) {
            updatedDomains = [domains];
        }
        
        const index = updatedDomains.indexOf(domain);
        if (index > -1) {
            updatedDomains.splice(index, 1);
        } else {
            updatedDomains.push(domain);
        }
        
        (product as any)['domains'] = updatedDomains;
    }

    async saveDomains(product: Product): Promise<void> {
        if (!product.id) {
            this.notificationService.show({
                type: 'error',
                message: 'Cannot save domains: Product ID is missing'
            });
            return;
        }

        const domains = (product as any)['domains'] || [];
        const productId = product.id;
        
        try {
            await this.productService.updateProductDomains(productId, domains);
            this.notificationService.show({
                type: 'success',
                message: 'Domains updated successfully'
            });
            this.editingDomains[productId] = false;
        } catch (error) {
            console.error('Error updating domains:', error);
            this.notificationService.show({
                type: 'error',
                message: 'Failed to update domains'
            });
        }
    }

    getDomainDisplay(product: Product): string {
        const domains = (product as any)['domain'] || (product as any)['domains'] || [];
        if (Array.isArray(domains)) {
            return domains.length > 0 ? `${domains.length} domain${domains.length === 1 ? '' : 's'}` : 'No domains';
        }
        return domains || 'No domains';
    }

    getDomainList(product: Product): string[] {
        const domains = (product as any)['domain'] || (product as any)['domains'] || [];
        if (Array.isArray(domains)) {
            return domains;
        }
        return domains ? [domains] : [];
    }

    openAddProductModal(): void {
        this.showAddProductModal = true;
    }

    closeAddProductModal(): void {
        this.showAddProductModal = false;
        this.resetNewProduct();
    }

    resetNewProduct(): void {
        this.newProduct = {
            sku: '',
            naam_nl: '',
            price: 0,
            stock_quantity: 0,
            stock_status: 'instock'
        };
    }

    // ── WooCommerce Sync Check ──────────────────────────────────────

    async openSyncCheck(product: Product): Promise<void> {
        this.syncProduct = product;
        this.syncCompareResult = null;
        this.showSyncModal = true;
        this.syncCheckLoading = true;

        try {
            this.syncCompareResult = await this.productService.compareWithWooCommerce(product.id!);
        } catch (error: any) {
            console.error('Sync check failed:', error);
            this.syncCompareResult = {
                linked: false,
                differences: [],
                message: error.message || 'Vergelijking mislukt'
            };
        } finally {
            this.syncCheckLoading = false;
        }
    }

    closeSyncModal(): void {
        this.showSyncModal = false;
        this.syncProduct = null;
        this.syncCompareResult = null;
    }

    async syncToWoo(): Promise<void> {
        if (!this.syncProduct?.id) return;
        this.syncToWooLoading = true;
        try {
            await this.productService.syncFirestoreToWoo(this.syncProduct.id);
            this.notificationService.show({
                type: 'success',
                message: 'Firestore data is naar WooCommerce gestuurd'
            });
            await this.openSyncCheck(this.syncProduct);
        } catch (error: any) {
            console.error('Sync FS→WC failed:', error);
            this.notificationService.show({
                type: 'error',
                message: `Sync mislukt: ${error.message || 'Onbekende fout'}`
            });
        } finally {
            this.syncToWooLoading = false;
        }
    }

    async syncFromWoo(): Promise<void> {
        if (!this.syncProduct?.id) return;
        this.syncFromWooLoading = true;
        try {
            await this.productService.syncWooToFirestore(this.syncProduct.id);
            this.notificationService.show({
                type: 'success',
                message: 'WooCommerce data is naar Firestore gehaald'
            });
            await this.openSyncCheck(this.syncProduct);
        } catch (error: any) {
            console.error('Sync WC→FS failed:', error);
            this.notificationService.show({
                type: 'error',
                message: `Sync mislukt: ${error.message || 'Onbekende fout'}`
            });
        } finally {
            this.syncFromWooLoading = false;
        }
    }

    // ── Add Product ───────────────────────────────────────────────────

    // ── Delete Product ─────────────────────────────────────────────────

    deleteConfirmProduct: Product | null = null;

    confirmDelete(product: Product): void {
        this.deleteConfirmProduct = product;
    }

    cancelDelete(): void {
        this.deleteConfirmProduct = null;
    }

    async deleteProduct(): Promise<void> {
        if (!this.deleteConfirmProduct?.id) return;
        const name = this.deleteConfirmProduct.acf?.naam_nl || this.deleteConfirmProduct.id;
        try {
            await this.productService.deleteProduct(this.deleteConfirmProduct.id);
            this.notificationService.show({
                type: 'success',
                message: `Product "${name}" is verwijderd`
            });
        } catch (error) {
            console.error('Error deleting product:', error);
            this.notificationService.show({
                type: 'error',
                message: 'Kon product niet verwijderen'
            });
        } finally {
            this.deleteConfirmProduct = null;
        }
    }

    // ── Add Product ───────────────────────────────────────────────────

    async saveNewProduct(): Promise<void> {
        if (!this.newProduct.naam_nl.trim()) {
            this.notificationService.show({
                type: 'error',
                message: 'Productnaam is verplicht'
            });
            return;
        }

        this.isAddingProduct = true;
        try {
            const productData: Partial<Product> = {
                sku: this.newProduct.sku,
                acf: {
                    naam_nl: this.newProduct.naam_nl
                },
                price: this.newProduct.price,
                stock_quantity: this.newProduct.stock_quantity,
                stock_status: this.newProduct.stock_status,
                status: 'publish'
            };

            const newId = await this.productService.addProduct(productData);

            // Sync to WooCommerce in the background
            this.productService.syncProductToWooCommerce(newId, productData).then(() => {
                this.notificationService.show({
                    type: 'success',
                    message: `Product is ook gesynchroniseerd naar WooCommerce`
                });
            }).catch((wcError) => {
                console.error('WooCommerce sync failed:', wcError);
                this.notificationService.show({
                    type: 'error',
                    message: 'Product aangemaakt in Firestore, maar WooCommerce sync is mislukt'
                });
            });

            this.notificationService.show({
                type: 'success',
                message: `Product "${this.newProduct.naam_nl}" is toegevoegd`
            });
            this.closeAddProductModal();
            this.router.navigate(['/webshop/edit', newId]);
        } catch (error) {
            console.error('Error adding product:', error);
            this.notificationService.show({
                type: 'error',
                message: 'Kon product niet toevoegen'
            });
        } finally {
            this.isAddingProduct = false;
        }
    }
} 